import { Component, EventEmitter, OnInit, Output, Input, OnChanges, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from 'src/app/models/constants';
import { EnvironmentPages } from 'src/app/models/environment-pages/environment-pages';
import { CloudProvider } from 'src/app/models/cloud-provider/cloud-provider';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { isReleaseVersion } from 'src/app/services/util/utils';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { translate } from '@jsverse/transloco';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ChangeDetectorRef } from '@angular/core';
import { DeploymentClass } from 'src/app/models/instance-class';
import { FeatureFlagsManagerService } from 'src/app/services/util/feature-flags-manager.service';
import { FeatureFlags } from 'src/app/models/constants/feature-flags.constant';

interface MenuItem {
  icon: string;
  label: string;
  active: boolean;
  page: EnvironmentPages;
  id: string;
  border?: boolean;
}

@Component({
  selector: 'app-environment-sidebar',
  templateUrl: './environment-sidebar.component.html',
  styleUrls: ['./environment-sidebar.component.scss'],
  standalone: false
})

export class EnvironmentSidebarComponent implements OnInit, OnChanges{
  @Output() toggle = new EventEmitter<void>();
  @Output() menuClick = new EventEmitter<string>();
  @Input() disableEfAndSanity = false;
  isCollapsed = false;
  Constants = Constants;
  activeIndex = 0;
  @Input() scheduleJobData: any;
  @Input() cloudProvider: CloudProvider;
  @Input() data:any;
  @Input() serviceVersions:any;
  @Input() isAWS:boolean;
  @Input() isExternalTenant;

  showSizingDocuments = false;
  EnvironmentPages = EnvironmentPages;
  DeployStatus = DeployStatus;
  solutionVersionLoaded = false;
  showDependencyTree = false;
  showEFTest = false;
  menuItems: MenuItem[] = [];
  lifecycleState: any;
  agentStatus: any;
  menuItemsLoading = true;
  skeletonItems = Array(4);
  private featureFlagsManagerService = inject(FeatureFlagsManagerService);
  private destroyRef = inject(DestroyRef);
  featureEnabledMap: { [key: string]: boolean } = {};

  readonly OP_TYPE_PAGE_MAP: Partial<Record<string, EnvironmentPages>> = {
    sanity_test:              EnvironmentPages.SANITY_TEST,
    ef_test:                  EnvironmentPages.EF_TEST,
    rvt_test:                 EnvironmentPages.PRODUCT_RVTS,
    config_rvt_test:          EnvironmentPages.RELEASE_PACKAGE,
    inventory:                EnvironmentPages.INVENTORY,
    backup_create:            EnvironmentPages.DATABASE_BACKUP,
    backup_delete:            EnvironmentPages.DATABASE_BACKUP,
    backup_update:            EnvironmentPages.DATABASE_BACKUP,
    cob_config:               EnvironmentPages.COB,
    cob_reconfig:             EnvironmentPages.COB,
    cob_update:               EnvironmentPages.COB,
    cob_delete:               EnvironmentPages.COB,
    runbook_provision:        EnvironmentPages.RUNBOOK,
    runbook_redeploy:         EnvironmentPages.RUNBOOK,
    runbook_config:           EnvironmentPages.RUNBOOK,
    runbook_update:           EnvironmentPages.RUNBOOK,
    runbook_delete:           EnvironmentPages.RUNBOOK,
    schedule_create:          EnvironmentPages.SCHEDULES,
    schedule_config_update:   EnvironmentPages.SCHEDULES,
    schedule_enable:          EnvironmentPages.SCHEDULES,
    schedule_disable:         EnvironmentPages.SCHEDULES,
    schedule_delete:          EnvironmentPages.SCHEDULES,
    scheduler_version_update: EnvironmentPages.SCHEDULES,
  };

  private readonly OP_TOOLTIP_KEYS: Partial<Record<string, { inprogress: string; failed: string }>> = {
    sanity_test:              { inprogress: 'BLUEPRINT_SUMMARY.SANITY_TEST_INPROGRESS',              failed: 'BLUEPRINT_SUMMARY.SANITY_TEST_FAILED' },
    ef_test:                  { inprogress: 'BLUEPRINT_SUMMARY.EF_TEST_INPROGRESS',                  failed: 'BLUEPRINT_SUMMARY.EF_TEST_FAILED' },
    config_rvt_test:          { inprogress: 'BLUEPRINT_SUMMARY.CONFIG_RVT_TEST_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.CONFIG_RVT_TEST_FAILED' },
    rvt_test:                 { inprogress: 'BLUEPRINT_SUMMARY.RVT_TEST_INPROGRESS',                 failed: 'BLUEPRINT_SUMMARY.RVT_TEST_FAILED' },
    inventory:                { inprogress: 'BLUEPRINT_SUMMARY.INVENTORY_INPROGRESS',                failed: 'BLUEPRINT_SUMMARY.INVENTORY_FAILED' },
    backup_create:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_CREATE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_CREATE_FAILED' },
    backup_delete:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_DELETE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_DELETE_FAILED' },
    backup_update:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_UPDATE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_UPDATE_FAILED' },
    cob_config:               { inprogress: 'BLUEPRINT_SUMMARY.COB_CONFIG_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_CONFIG_FAILED' },
    cob_reconfig:             { inprogress: 'BLUEPRINT_SUMMARY.COB_RECONFIG_INPROGRESS',             failed: 'BLUEPRINT_SUMMARY.COB_RECONFIG_FAILED' },
    cob_update:               { inprogress: 'BLUEPRINT_SUMMARY.COB_UPDATE_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_UPDATE_FAILED' },
    cob_delete:               { inprogress: 'BLUEPRINT_SUMMARY.COB_DELETE_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_DELETE_FAILED' },
    runbook_provision:        { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_PROVISION_INPROGRESS',        failed: 'BLUEPRINT_SUMMARY.RUNBOOK_PROVISION_FAILED' },
    runbook_redeploy:         { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_REDEPLOY_INPROGRESS',         failed: 'BLUEPRINT_SUMMARY.RUNBOOK_REDEPLOY_FAILED' },
    runbook_config:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_CONFIG_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_CONFIG_FAILED' },
    runbook_update:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_UPDATE_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_UPDATE_FAILED' },
    runbook_delete:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_DELETE_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_DELETE_FAILED' },
    schedule_create:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_CREATE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_CREATE_FAILED' },
    schedule_config_update:   { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_CONFIG_UPDATE_INPROGRESS',   failed: 'BLUEPRINT_SUMMARY.SCHEDULE_CONFIG_UPDATE_FAILED' },
    schedule_enable:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_ENABLE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_ENABLE_FAILED' },
    schedule_disable:         { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_DISABLE_INPROGRESS',         failed: 'BLUEPRINT_SUMMARY.SCHEDULE_DISABLE_FAILED' },
    schedule_delete:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_DELETE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_DELETE_FAILED' },
    scheduler_version_update: { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULER_VERSION_UPDATE_INPROGRESS', failed: 'BLUEPRINT_SUMMARY.SCHEDULER_VERSION_UPDATE_FAILED' },
  };

  get inprogressPage(): EnvironmentPages | null {
    if (this.data?.status !== DeployStatus.INPROGRESS) return null;
    return this.OP_TYPE_PAGE_MAP[this.data?.lastOperation?.type] ?? null;
  }

  get failedPage(): EnvironmentPages | null {
    if (this.data?.status !== DeployStatus.DEPLOYED) return null;
    if (this.data?.lastOperation?.status?.toLowerCase() !== 'failed') return null;
    return this.OP_TYPE_PAGE_MAP[this.data?.lastOperation?.type] ?? null;
  }

  getOpTooltip(opStatus: 'inprogress' | 'failed'): string {
    const keys = this.OP_TOOLTIP_KEYS[this.data?.lastOperation?.type];
    return keys ? translate(keys[opStatus]) : '';
  }

  private readonly tsiTssRestrictedPages = [
    EnvironmentPages.RELEASE_PACKAGE,
    EnvironmentPages.SFTP,
    EnvironmentPages.DATABASE_BACKUP,
    EnvironmentPages.BASE_IMAGE,
    EnvironmentPages.PRODUCT_RVTS,
    EnvironmentPages.EF_TEST,
    EnvironmentPages.COB,
    EnvironmentPages.RUNBOOK,
    EnvironmentPages.SCHEDULES,
    EnvironmentPages.JOBS,
    EnvironmentPages.CUSTOM_PACKAGE,
    EnvironmentPages.SIZING_DOCUMENTS,
    EnvironmentPages.METRICS,
    EnvironmentPages.UPDATE_MANAGER,
    EnvironmentPages.LOCAL_CONFIGURATION
  ];

  private readonly classERestrictedPages = [
    EnvironmentPages.RELEASE_PACKAGE,
    EnvironmentPages.SFTP,
    EnvironmentPages.DATABASE_BACKUP,
    EnvironmentPages.BASE_IMAGE,
    EnvironmentPages.PRODUCT_RVTS,
    EnvironmentPages.EF_TEST,
    EnvironmentPages.COB,
    EnvironmentPages.RUNBOOK,
    EnvironmentPages.SCHEDULES,
    EnvironmentPages.JOBS,
    EnvironmentPages.CUSTOM_PACKAGE,
    EnvironmentPages.SIZING_DOCUMENTS,
    EnvironmentPages.METRICS,
    EnvironmentPages.UPDATE_MANAGER,
    EnvironmentPages.LOCAL_CONFIGURATION,
    EnvironmentPages.SANITY_TEST,
    EnvironmentPages.INVENTORY,
    EnvironmentPages.DEPENDENCY_TREE,
    EnvironmentPages.START_STOP,
    EnvironmentPages.VERSION_MANAGER,
    EnvironmentPages.SETTINGS,
    EnvironmentPages.ENDPOINTS
  ];

  constructor(private solutionsService: SolutionsService, private instancesService: InstancesService,
    private cdr: ChangeDetectorRef) {
    const env = window.location.href.split('.')[0].split('/')[2];
    this.showDependencyTree = env === 'dev' || env === 'preprod' || env === 'utp';
  }


  ngOnInit(): void {
    if (this.data) {
      this.getFeatureByInstanceid(this.data.id);
      this.initializeMenuItems();
      this.menuItemsLoading = false;
    }
    if (this.data?.solutionVersionId) {
      this.showEFTest = (this.data.deploymentClass === 'D' && this.data.type === 'dev');
      this.getSolutionVersion(this.data?.solutionVersionId);
    }
    this.showSizingDocuments = this.isAWS ? false : true;
    if (this.data?.id) {
      this.fetchLifecycleState(this.data.id);
    }
  }

  private initializeMenuItems(): void {
    if (this.isExternalTenant) {
      this.menuItems = [
      { icon: 'summarize', label: 'Overview', active: true, page: EnvironmentPages.OVERVIEW, id: 'env-nav-overview'},
      { icon: 'monitor', label: 'Endpoints', active: false, page: EnvironmentPages.ENDPOINTS, id: 'env-nav-endpoints'},
      { icon: 'view_timeline', label: 'Activity', active: false, page: EnvironmentPages.ACTIVITY, id: 'env-nav-activity'},
      { icon: 'inventory_2', label: 'Inventory', active: false, page: EnvironmentPages.INVENTORY, id: 'env-nav-inventory'},
      ];
    }
      else {
      this.menuItems = [
      { icon: 'summarize', label: 'Overview', active: true, page: EnvironmentPages.OVERVIEW, id: 'env-nav-overview'},
      { icon: 'monitor', label: 'Endpoints', active: false, page: EnvironmentPages.ENDPOINTS, id: 'env-nav-endpoints'},
      { icon: 'newRelease', label: 'Release Package', active: false, page: EnvironmentPages.RELEASE_PACKAGE, id: 'env-nav-release-package'},
      { icon: 'security', label: 'SFTP', active: false, border: true, page: EnvironmentPages.SFTP, id: 'env-nav-sftp'},
      { icon: 'image', label: 'Base Image', active: false, border: true, page: EnvironmentPages.BASE_IMAGE, id: 'env-nav-base-image'},

      { icon: 'dns', label: 'Database Backup', active: false, page: EnvironmentPages.DATABASE_BACKUP, id: 'env-nav-database-backup'},
      { icon: 'fact_check', label: 'Product RVTs', active: false, page: EnvironmentPages.PRODUCT_RVTS, id: 'env-nav-product-rvts'},
      { icon: 'checklist', label: 'Sanity Test', active: false, page: EnvironmentPages.SANITY_TEST, id: 'env-nav-sanity-test'},
      { icon: 'find_in_page', label: 'EF Test', active: false, border: true, page: EnvironmentPages.EF_TEST, id: 'env-nav-ef-test'},
      { icon: 'work_off', label: 'COB', active: false, border: true, page: EnvironmentPages.COB, id: 'env-nav-cob'},
      { icon: 'assignment', label: 'Runbook', active: false, page: EnvironmentPages.RUNBOOK, id: 'env-nav-runbook'},
      { icon: 'schedule', label: 'Schedules', active: false, page: EnvironmentPages.SCHEDULES, id: 'env-nav-schedules'},
      { icon: 'work_outline', label: 'Jobs', active: false, border: true, page: EnvironmentPages.JOBS, id: 'env-nav-jobs'},
      // { icon: 'web_stories', label: 'Component Update', active: false, border: false, page: EnvironmentPages.COMPONENT_UPDATE},
      { icon: 'book', label: 'Custom Package', active: false, page: EnvironmentPages.CUSTOM_PACKAGE, id: 'env-nav-custom-package'},


      { icon: 'document_scanner', label: 'Sizing Documents', active: false, border: true, page: EnvironmentPages.SIZING_DOCUMENTS, id: 'env-nav-sizing-documents'},

      { icon: 'view_timeline', label: 'Activity', active: false, page: EnvironmentPages.ACTIVITY, id: 'env-nav-activity'},
      { icon: 'inventory_2', label: 'Inventory', active: false, page: EnvironmentPages.INVENTORY, id: 'env-nav-inventory'},
      { icon: 'analytics', label: 'Metrics', active: false, page: EnvironmentPages.METRICS, id: 'env-nav-metrics'},
      { icon: 'settings_backup_restore', label: 'Update Manager', active: false, page: EnvironmentPages.UPDATE_MANAGER, id: 'env-nav-update-manager'},
      { icon: 'subtitles', label: 'Local Configuration', active: false, page: EnvironmentPages.LOCAL_CONFIGURATION, id: 'env-nav-local-configuration'},
      { icon: 'settings', label: 'Settings', active: false, page: EnvironmentPages.SETTINGS, id: 'env-nav-settings'},
      { icon: 'layers', label: 'Dependency Tree', active: false, page: EnvironmentPages.DEPENDENCY_TREE, id: 'env-nav-dependency-tree'},
    ];
    }
    if (this.data?.solutionVersionId) {
      this.showEFTest = (this.data.deploymentClass === 'D' && this.data.type === 'dev');
      this.getSolutionVersion(this.data?.solutionVersionId);
    }
    this.showSizingDocuments = this.isAWS ? false : true;
    if (history.state.data === EnvironmentPages.RUNBOOK) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.RUNBOOK).active = true;
      this.activeIndex =  this.menuItems.findIndex(item => item.page === EnvironmentPages.RUNBOOK);
    } else if (history.state.data === EnvironmentPages.SCHEDULES) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.SCHEDULES).active = true;
      this.activeIndex =  this.menuItems.findIndex(item => item.page === EnvironmentPages.SCHEDULES);
    } else if (history.state.data === EnvironmentPages.JOBS) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.JOBS).active = true;
      this.activeIndex =  this.menuItems.findIndex(item => item.page === EnvironmentPages.JOBS);
    } else if (history.state.data === EnvironmentPages.SIZING_DOCUMENTS) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.SIZING_DOCUMENTS).active = true;
      this.activeIndex = this.menuItems.findIndex(item => item.page === EnvironmentPages.SIZING_DOCUMENTS);
    } else if (history.state.data === EnvironmentPages.BASE_IMAGE) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.BASE_IMAGE).active = true;
      this.activeIndex = this.menuItems.findIndex(item => item.page === EnvironmentPages.BASE_IMAGE);
    }

    if (this.data?.id) {
      this.fetchLifecycleState(this.data.id);
    }
  }

  ngOnChanges() {
    if (this.data && this.menuItemsLoading) {
      this.getFeatureByInstanceid(this.data.id);
      this.initializeMenuItems();
      this.menuItemsLoading = false;
      this.showEFTest = (this.data.deploymentClass === 'D' && this.data.type === 'dev');
    }
    if (this.data?.solutionVersionId) {
      this.getSolutionVersion(this.data.solutionVersionId);
    }
    if (this.data?.id) {
      this.fetchLifecycleState(this.data.id);
    }
   if (this.scheduleJobData) {
      this.menuItems.map(item => item.active = false);
      this.menuItems.find(item => item.page === EnvironmentPages.JOBS).active = true;
      this.activeIndex =  this.menuItems.findIndex(item => item.page === EnvironmentPages.JOBS);
    }
}

  fetchLifecycleState(id: string): void {
    this.instancesService.getInstanceById(id).subscribe({
      next: (instance: any) => {
        this.agentStatus = instance?.agentStatus ?? null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.agentStatus = null;
        this.cdr.detectChanges();
      }
    });
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.toggle.emit();
  }

  canAccessFeature(flagId: string): boolean {
    const isEnabled = this.featureFlagsManagerService.isFeatureEnabled(flagId);
    return isEnabled;
  }

  isMenuVisible(item: { page: EnvironmentPages; border?: boolean }): boolean {
    if (this.data?.deploymentClass === DeploymentClass.E && this.classERestrictedPages.includes(item.page)) {
      return false;
    }


    const isTsiOrTss = this.data?.type === 'tsi' || this.data?.type === 'tss';
    if (isTsiOrTss && this.tsiTssRestrictedPages.includes(item.page)) {
      return false;
    }

     // Check feature flag for BASE_IMAGE - only hide if flags are loaded and feature is disabled
    if (!isTsiOrTss && item.page === EnvironmentPages.BASE_IMAGE ) {
        const isEnabled = this.canAccessFeature(FeatureFlags.BASE_IMAGE_UPDATE);
        const instanceFeatureEnabled = this.isFeatureAccessible(FeatureFlags.BASE_IMAGE_UPDATE);
        return isEnabled && instanceFeatureEnabled; // Only show if feature is enabled and instance feature flag is true 
    }

    if (!isTsiOrTss && item.page === EnvironmentPages.COB ) {
        const isEnabled = this.canAccessFeature(FeatureFlags.AZURE_COB) || this.isAWS;
        return isEnabled;
    }

    if (this.data?.type === 'tsi' && item.page === EnvironmentPages.SANITY_TEST) {
      return false;
    }

    if(this.data?.type !== 'tss' && item.page === EnvironmentPages.SANITY_TEST){
       const isTenantFeatureEnabled = this.canAccessFeature(FeatureFlags.ENV_SANITY_TEST_AWS) || !this.isAWS;
       const instanceFeatureEnabled = this.isFeatureAccessible(FeatureFlags.ENV_SANITY_TEST_AWS) || !this.isAWS;
       return isTenantFeatureEnabled && instanceFeatureEnabled; // Only show if tenant and instance feature flags are enabled or if not AWS
    }

    if (item.page === EnvironmentPages.INVENTORY && this.isExternalTenant && !this.agentStatus) {
      return false;
    }

    if ((item.page === EnvironmentPages.RUNBOOK || item.page === EnvironmentPages.SCHEDULES || item.page === EnvironmentPages.JOBS) && !this.isAWS) {
      return this.canAccessFeature(FeatureFlags.AZURE_RUNBOOK);
    }

    if (this.cloudProvider === CloudProvider.AWS) {
      return item.page !== EnvironmentPages.COB;
    }

    return true;
  }


  activeMenu(activeItem) {
    this.menuItems.forEach(element => {
      element.active = false;
    });
    this.menuItems.find(item => item.page === activeItem.page).active = true;
    this.moveFocus(undefined);
  }

  /**
   * Tab up/down funcationality for navigation menu
   * @param event - keyboard event
   * @param index - index of menu item
   */
  onKeydown(event: KeyboardEvent, index: number) {
    const key = event.key;
    if (event.key === 'Tab') {
      event.preventDefault();

      if (event.shiftKey) {
        this.moveFocus(index - 1);
      } else {
        this.moveFocus(index + 1);
      }
    }

    else if (key === 'Enter' || key === ' ') {
      this.activeMenu(this.menuItems[index]);
      this.menuClick.emit(this.menuItems[index].page);
      event.preventDefault();
    }
  }

  /**
   * Focus on menu items
   * @param newIndex - index of focused item
   */
  private moveFocus(newIndex: number) {
    const itemCount = this.menuItems.length;
    if (newIndex >= itemCount) newIndex = 0;
    if (newIndex < 0) newIndex = itemCount - 1;

    this.activeIndex = newIndex;
    setTimeout(() => {
      const items = document.querySelectorAll('.sidebar .menu li');
      const el = items[newIndex] as HTMLElement;
      el?.focus();
    });
  }
  // displayed sizing doc menu based on solution version
  getSolutionVersion(solutionVersionId: string) {
    const solnVersion = this.serviceVersions?.find(v => v.id === solutionVersionId);
    const version = solnVersion?.version;
    if (version) {
      this.showSizingDocuments = isReleaseVersion(version) && !this.isAWS;
      this.solutionVersionLoaded = true;
    };
  }

  getFeatureByInstanceid(instanceId: string): void {
    if (!instanceId) {
      return;
    }
    this.featureFlagsManagerService.loadInstanceFeatureFlags(instanceId);
    this.featureFlagsManagerService.instanceFeatureFlags$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(flags => {
        flags.forEach(flag => {
          this.featureEnabledMap[flag.id] = (flag.enabled === true || flag.enabled === 'true');
        });
        this.cdr.detectChanges();
      });
  }

  isFeatureAccessible(featureId: string | FeatureFlags): boolean {
    return this.featureEnabledMap[featureId] ?? false;
  }

}
